/**
 * VAT Breakdown Detail Component
 * 
 * Detailed view of VAT breakdown by tax rate with visualization.
 * Shows sales, purchases, and VAT amounts for each tax rate.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../hooks/useOrganization';
import {
  getVatBreakdown,
  recalculateVatReturn,
  type VatBreakdownResponse,
} from '../api';

interface VatBreakdownDetailProps {
  vatReturnId: string;
  onBack?: () => void;
  onRecalculated?: () => void;
}

export default function VatBreakdownDetail({ vatReturnId, onBack, onRecalculated }: VatBreakdownDetailProps) {
  const { t } = useTranslation();
  const { currentOrganization } = useOrganization();
  const [data, setData] = useState<VatBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    if (currentOrganization && vatReturnId) {
      loadBreakdown();
    }
  }, [currentOrganization, vatReturnId]);

  const loadBreakdown = async () => {
    if (!currentOrganization || !vatReturnId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getVatBreakdown(vatReturnId, currentOrganization.id);
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load VAT breakdown');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    if (!currentOrganization || !vatReturnId) return;
    setRecalculating(true);
    try {
      await recalculateVatReturn(vatReturnId, currentOrganization.id);
      await loadBreakdown();
      onRecalculated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to recalculate');
    } finally {
      setRecalculating(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount / 100);
  };

  const getVatRateColor = (rate: number) => {
    switch (rate) {
      case 25.5:
        return 'bg-blue-500';
      case 14:
        return 'bg-green-500';
      case 10:
        return 'bg-yellow-500';
      case 0:
        return 'bg-gray-400';
      default:
        return 'bg-gray-500';
    }
  };

  const getVatRateBg = (rate: number) => {
    switch (rate) {
      case 25.5:
        return 'bg-blue-50';
      case 14:
        return 'bg-green-50';
      case 10:
        return 'bg-yellow-50';
      case 0:
        return 'bg-gray-50';
      default:
        return 'bg-gray-50';
    }
  };

  // Calculate max value for bar chart scaling
  const maxSalesAmount = data?.breakdown.reduce((max, item) => 
    Math.max(max, item.sales.amountExclVat), 0) || 0;
  const maxPurchaseAmount = data?.breakdown.reduce((max, item) => 
    Math.max(max, item.purchases.amountExclVat), 0) || 0;

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin inline-block w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
        <p className="mt-2 text-gray-600">Ladataan ALV-erittelyä...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <p className="font-medium">Virhe ladattaessa ALV-erittelyä</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={loadBreakdown}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Yritä uudelleen
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center text-gray-500">
        <p>Ei dataa saatavilla</p>
      </div>
    );
  }

  const isFiled = data.vatReturn.status === 'filed' || data.vatReturn.status === 'locked';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            {onBack && (
              <button
                onClick={onBack}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                ← Takaisin
              </button>
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            ALV-erittely verokannoittain
          </h1>
          <p className="text-gray-600">
            Kausi: {new Date(data.vatReturn.periodStart).toLocaleDateString('fi-FI')} - {' '}
            {new Date(data.vatReturn.periodEnd).toLocaleDateString('fi-FI')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            data.vatReturn.status === 'filed' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-blue-100 text-blue-800'
          }`}>
            {data.vatReturn.status === 'draft' && t('common.draft')}
            {data.vatReturn.status === 'calculated' && 'Laskettu'}
            {data.vatReturn.status === 'filed' && 'Ilmoitettu'}
            {data.vatReturn.status === 'locked' && 'Lukittu'}
          </span>
          {!isFiled && (
            <button
              onClick={handleRecalculate}
              disabled={recalculating}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {recalculating ? 'Lasketaan...' : 'Laske uudelleen'}
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 mb-1">Myyntien veroton määrä</p>
          <p className="text-xl font-bold text-gray-900">{formatCurrency(data.summary.totalSalesExclVat)}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 mb-1">Myyntien ALV</p>
          <p className="text-xl font-bold text-red-600">{formatCurrency(data.summary.totalSalesVat)}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 mb-1">Ostojen veroton määrä</p>
          <p className="text-xl font-bold text-gray-900">{formatCurrency(data.summary.totalPurchasesExclVat)}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 mb-1">Ostojen ALV (vähennys)</p>
          <p className="text-xl font-bold text-green-600">{formatCurrency(data.summary.totalPurchasesVat)}</p>
        </div>
      </div>

      {/* Net VAT Summary */}
      <div className={`rounded-lg p-6 mb-8 ${
        data.summary.netVat >= 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-medium ${
              data.summary.netVat >= 0 ? 'text-red-700' : 'text-green-700'
            }`}>
              {data.summary.netVat >= 0 
                ? 'ALV maksettava verottajalle:' 
                : 'ALV palautettava verottajalta:'}
            </p>
            <p className={`text-3xl font-bold mt-1 ${
              data.summary.netVat >= 0 ? 'text-red-600' : 'text-green-600'
            }`}>
              {formatCurrency(Math.abs(data.summary.netVat))}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Ilmoitusperuste</p>
            <p className="font-medium">
              {data.vatReturn.reportingBasis === 'accrual' ? 'Suoriteperuste' : 'Maksuperuste'}
            </p>
          </div>
        </div>
      </div>

      {/* Visualization */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Sales by VAT Rate Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Myynnit verokannoittain</h3>
          <div className="space-y-4">
            {data.breakdown.map((item) => (
              <div key={`sales-${item.vatRate}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{item.vatRate}%</span>
                  <span className="text-sm text-gray-600">{formatCurrency(item.sales.amountExclVat)}</span>
                </div>
                <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getVatRateColor(item.vatRate)} transition-all duration-500`}
                    style={{
                      width: maxSalesAmount > 0 
                        ? `${(item.sales.amountExclVat / maxSalesAmount) * 100}%` 
                        : '0%'
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  ALV: {formatCurrency(item.sales.vatAmount)}
                </p>
              </div>
            ))}
            {data.breakdown.length === 0 && (
              <p className="text-gray-500 text-center py-4">Ei myyntitapahtumia</p>
            )}
          </div>
        </div>

        {/* Purchases by VAT Rate Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Ostot verokannoittain</h3>
          <div className="space-y-4">
            {data.breakdown.map((item) => (
              <div key={`purchase-${item.vatRate}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{item.vatRate}%</span>
                  <span className="text-sm text-gray-600">{formatCurrency(item.purchases.amountExclVat)}</span>
                </div>
                <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getVatRateColor(item.vatRate)} transition-all duration-500`}
                    style={{
                      width: maxPurchaseAmount > 0 
                        ? `${(item.purchases.amountExclVat / maxPurchaseAmount) * 100}%` 
                        : '0%'
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  ALV (vähennys): {formatCurrency(item.purchases.vatAmount)}
                </p>
              </div>
            ))}
            {data.breakdown.length === 0 && (
              <p className="text-gray-500 text-center py-4">Ei ostotapahtumia</p>
            )}
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Tarkka erittely verokannoittain</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Verokanta
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Koodi
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Myynti (veroton)
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Myynnin ALV
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Osto (veroton)
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Oston ALV
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Netto ALV
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.breakdown.map((item) => (
                <tr key={item.vatRate} className={`${getVatRateBg(item.vatRate)} hover:bg-opacity-75`}>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center">
                      <span className={`w-3 h-3 rounded-full mr-2 ${getVatRateColor(item.vatRate)}`}></span>
                      <span className="font-medium text-gray-900">{item.vatRate}%</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">{item.vatCode}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {formatCurrency(item.sales.amountExclVat)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-red-600">
                    {item.sales.vatAmount > 0 ? '+' : ''}{formatCurrency(item.sales.vatAmount)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-900">
                    {formatCurrency(item.purchases.amountExclVat)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-green-600">
                    {item.purchases.vatAmount > 0 ? '-' : ''}{formatCurrency(item.purchases.vatAmount)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium">
                    <span className={item.sales.vatAmount - item.purchases.vatAmount >= 0 ? 'text-red-600' : 'text-green-600'}>
                      {formatCurrency(item.sales.vatAmount - item.purchases.vatAmount)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-100 font-semibold">
              <tr>
                <td className="px-4 py-3 text-gray-900" colSpan={2}>Yhteensä</td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {formatCurrency(data.summary.totalSalesExclVat)}
                </td>
                <td className="px-4 py-3 text-right text-red-600">
                  +{formatCurrency(data.summary.totalSalesVat)}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {formatCurrency(data.summary.totalPurchasesExclVat)}
                </td>
                <td className="px-4 py-3 text-right text-green-600">
                  -{formatCurrency(data.summary.totalPurchasesVat)}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className={data.summary.netVat >= 0 ? 'text-red-600' : 'text-green-600'}>
                    {formatCurrency(data.summary.netVat)}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* VAT Codes Legend */}
      <div className="mt-8 bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Verokoodien selitykset</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600">
          <div><span className="font-mono">301</span> = Myynti 25,5%</div>
          <div><span className="font-mono">302</span> = Myynti 14%</div>
          <div><span className="font-mono">303</span> = Myynti 10%</div>
          <div><span className="font-mono">304</span> = Myynti 0%</div>
          <div><span className="font-mono">401</span> = Ostot 25,5%</div>
          <div><span className="font-mono">402</span> = Ostot 14%</div>
          <div><span className="font-mono">403</span> = Ostot 10%</div>
        </div>
      </div>
    </div>
  );
}
